import { Spinner } from '@heroui/react';
import { FirebaseError } from 'firebase/app';
import { AlertCircle, CheckCircle, Eye, EyeOff, Lock } from 'lucide-react';
import { FC, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { auth } from '@/config';
import { useActivateAccount, useHeroToast, useSetUserPassword, useVerifyInviteToken } from '@/hooks';
import { useUserState } from '@/state';
import { LocaleLink, useLocaleNavigate } from '@/utils/locale';

import { ReactComponent as Logo } from '../../../assets/logo-hs.svg';
import {
  authAccentText,
  AuthContainer,
  AuthForm,
  FormContainer,
  FormWrapper,
  InputGroup,
  InputIcon,
  InputLabel,
  InputWrapper,
  LogoWrapper,
  MarketingContent,
  MarketingDescription,
  MarketingSection,
  MarketingTitle,
  PasswordToggle,
  SignUpLink,
  StyledInput,
  SubmitButton,
  WelcomeSubtitle,
  WelcomeTitle,
} from '../auth.styled';

interface ResetPasswordFormProps {
  password: string;
  confirmPassword: string;
}

const PasswordRequirement = styled.div<{ $met: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${({ $met, theme }) => ($met
    ? (theme.mode === 'dark' ? '#86efac' : '#16a34a')
    : (theme.mode === 'dark' ? '#9ca3af' : '#6b7280'))
};
  transition: color 0.2s ease;

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
`;

const PasswordRequirements = styled.div`
  padding: 8px 0;
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ErrorMessage = styled.p`
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
`;

const TokenErrorContainer = styled.div`
  text-align: center;
  padding: 40px 20px;
`;

const TokenErrorIcon = styled(AlertCircle)`
  color: #ef4444;
  margin: 0 auto 24px;
`;

const TokenErrorTitle = styled.h2`
  font-size: 24px;
  font-weight: 300;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f3f4f6' : '#1f2937')};
  margin-bottom: 12px;
`;

const TokenErrorMessage = styled.p`
  font-size: 16px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  margin-bottom: 32px;
  line-height: 1.5;
`;

const SupportEmailLink = styled.a`
  color: ${({ theme }) => authAccentText(theme.mode)};
  text-decoration: none;
  font-weight: 300;
  transition: text-decoration 0.2s ease;

  &:hover {
    text-decoration: underline;
  }
`;

export const ResetPassword: FC = () => {
  const { t } = useTranslation();
  const { register, handleSubmit, watch, formState: { errors } } = useForm<ResetPasswordFormProps>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const { showError, showSuccess } = useHeroToast();
  const { confirmPasswordReset } = useUserState();
  const { mutateAsync: activateAccount } = useActivateAccount();
  const { mutateAsync: verifyInviteToken } = useVerifyInviteToken();
  const { mutateAsync: setUserPassword } = useSetUserPassword();
  const navigate = useLocaleNavigate();
  const [searchParams] = useSearchParams();

  const password = watch('password');
  const confirmPassword = watch('confirmPassword');
  const oobCode = searchParams.get('oobCode');
  const mode = searchParams.get('mode');
  const token = searchParams.get('token');

  // Handle email verification
  useEffect(() => {
    if (mode === 'verifyEmail' && oobCode) {
      // Redirect to verify email success handler
      navigate(`/verify-email-success?oobCode=${oobCode}&mode=${mode}`);
    }
  }, [mode, oobCode, navigate]);

  // Token verification state
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userUid, setUserUid] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(!!token);
  const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);

  // Password requirements
  const hasMinLength = password?.length >= 8;
  const hasUpperCase = /[A-Z]/.test(password || '');
  const hasLowerCase = /[a-z]/.test(password || '');
  const hasNumber = /\d/.test(password || '');
  const passwordsMatch = password === confirmPassword && password?.length > 0;

  const isFormValid = hasMinLength && hasUpperCase && hasLowerCase && hasNumber && passwordsMatch;

  const hasRedirected = useRef(false);
  const hasVerified = useRef(false);

  // Verify token if present (invite system) - run once
  useEffect(() => {
    if (hasVerified.current) return;
    if (!token) return;
    hasVerified.current = true;

    const verifyToken = async () => {
      try {
        const result = await verifyInviteToken({ token });
        setUserEmail(result.email || null);
        setUserUid(result.uid || null);
        setIsTokenValid(true);
      } catch (_error) {
        setIsTokenValid(false);
        showError(t('auth.resetPassword.invalidLink'));
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Validate oobCode or token presence - run once
  useEffect(() => {
    if (hasRedirected.current) return;
    if (!oobCode && !token && !isVerifying) {
      hasRedirected.current = true;
      showError(t('auth.resetPassword.invalidLink'));
      navigate('/login');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oobCode, token, isVerifying]);

  const getFirebaseErrorMessage = (error: unknown): string => {
    if (error instanceof FirebaseError) {
      const errorKey = `auth.errors.${error.code}`;
      const translated = t(errorKey);
      if (translated !== errorKey) {
        return translated;
      }
    }
    return t('auth.errors.default');
  };

  const onSubmit = async (formData: ResetPasswordFormProps): Promise<void> => {
    setIsSubmitting(true);

    try {
      // Scenario 1: Invite token system (new user activation)
      if (token && userUid && userEmail && isTokenValid) {
        await setUserPassword({ uid: userUid, newPassword: formData.password, token });
        await activateAccount({ email: userEmail });

        const { signInWithEmailAndPassword } = await import('firebase/auth');
        await signInWithEmailAndPassword(auth, userEmail, formData.password);

        setResetSuccess(true);
        showSuccess(t('auth.resetPassword.success'));
        setTimeout(() => navigate('/'), 3000);
      }
      // Scenario 2: Firebase password reset (oobCode)
      else if (oobCode) {
        await confirmPasswordReset(oobCode, formData.password);
        setResetSuccess(true);
        showSuccess(t('auth.resetPassword.success'));
        setTimeout(() => navigate('/login'), 3000);
      }
      // Scenario 3: No valid code/token
      else {
        showError(t('auth.resetPassword.invalidLink'));
      }
    } catch (error) {
      showError(getFirebaseErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state - verifying token
  if (isVerifying) {
    return (
      <AuthContainer>
        <FormContainer>
          <FormWrapper>
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spinner size="lg" />
              <WelcomeSubtitle style={{ marginTop: '20px' }}>
                {t('auth.resetPassword.verifyingToken', 'Verifying your invitation...')}
              </WelcomeSubtitle>
            </div>
          </FormWrapper>
        </FormContainer>
        <MarketingSection>
          <MarketingContent>
            <MarketingTitle>
              {t('auth.resetPassword.marketingTitle')}
            </MarketingTitle>
            <MarketingDescription>
              {t('auth.resetPassword.marketingDescription')}
            </MarketingDescription>
          </MarketingContent>
        </MarketingSection>
      </AuthContainer>
    );
  }

  // Invalid or expired token state
  if (token && isTokenValid === false && !isVerifying) {
    return (
      <AuthContainer>
        <FormContainer>
          <FormWrapper>

            <TokenErrorContainer>
              <TokenErrorIcon size={64} />
              <TokenErrorTitle>
                {t('auth.resetPassword.tokenExpiredTitle', 'Link wygasł lub został już użyty')}
              </TokenErrorTitle>
              <TokenErrorMessage>
                {t('auth.resetPassword.tokenExpiredMessage', 'Ten link do resetowania hasła wygasł lub został już wykorzystany. Skontaktuj się z administratorem: ')}{' '}
                <SupportEmailLink href="mailto:support@hotelspot.pl">
                  support@hotelspot.pl
                </SupportEmailLink>
                {t('auth.resetPassword.tokenExpiredMessageSuffix', ', aby otrzymać nowy link.')}
              </TokenErrorMessage>

              <SignUpLink style={{ marginTop: '16px' }}>
                {t('auth.resetPassword.backToLogin', 'Powrót do logowania')}{' '}
                <LocaleLink to="/login">{t('auth.resetPassword.signIn', 'Zaloguj się')}</LocaleLink>
              </SignUpLink>
            </TokenErrorContainer>
          </FormWrapper>
        </FormContainer>
        <MarketingSection>
          <MarketingContent>
            <MarketingTitle>
              {t('auth.resetPassword.marketingTitle')}
            </MarketingTitle>
            <MarketingDescription>
              {t('auth.resetPassword.marketingDescription')}
            </MarketingDescription>
          </MarketingContent>
        </MarketingSection>
      </AuthContainer>
    );
  }

  // Success state
  if (resetSuccess) {
    return (
      <AuthContainer>
        <FormContainer>
          <FormWrapper>
            <LogoWrapper>
              <Logo />
            </LogoWrapper>

            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <CheckCircle
                size={64}
                style={{
                  color: '#16a34a',
                  margin: '0 auto 24px',
                }}
              />
              <WelcomeTitle>{t('auth.resetPassword.successTitle')}</WelcomeTitle>
              <WelcomeSubtitle>
                {t('auth.resetPassword.successDescription')}
              </WelcomeSubtitle>
            </div>
          </FormWrapper>
        </FormContainer>
        <MarketingSection>
          <MarketingContent>
            <MarketingTitle>
              {t('auth.resetPassword.marketingTitle')}
            </MarketingTitle>
            <MarketingDescription>
              {t('auth.resetPassword.marketingDescription')}
            </MarketingDescription>
          </MarketingContent>
        </MarketingSection>
      </AuthContainer>
    );
  }

  return (
    <AuthContainer>
      <FormContainer>
        <FormWrapper>
          <LogoWrapper>
            <Logo />
          </LogoWrapper>

          <WelcomeTitle>{t('auth.resetPassword.title')}</WelcomeTitle>
          <WelcomeSubtitle>
            {t('auth.resetPassword.subtitle')}
          </WelcomeSubtitle>

          <AuthForm onSubmit={handleSubmit(onSubmit)}>
            <InputGroup>
              <InputLabel>{t('auth.resetPassword.newPasswordLabel')}</InputLabel>
              <InputWrapper>
                <InputIcon>
                  <Lock />
                </InputIcon>
                <StyledInput
                  type={isPasswordVisible ? 'text' : 'password'}
                  placeholder={t('auth.resetPassword.newPasswordPlaceholder')}
                  {...register('password', {
                    required: t('auth.resetPassword.passwordRequired'),
                    minLength: {
                      value: 8,
                      message: t('auth.resetPassword.passwordMinLength'),
                    },
                  })}
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                >
                  {isPasswordVisible ? <EyeOff /> : <Eye />}
                </PasswordToggle>
              </InputWrapper>
              {errors.password && <ErrorMessage>{errors.password.message}</ErrorMessage>}

              <PasswordRequirements>
                <PasswordRequirement $met={hasMinLength}>
                  <CheckCircle />
                  {t('auth.resetPassword.requirementMinLength', 'Minimum 8 znaków')}
                </PasswordRequirement>
                <PasswordRequirement $met={hasUpperCase}>
                  <CheckCircle />
                  {t('auth.resetPassword.requirementUpperCase', 'Jedna wielka litera')}
                </PasswordRequirement>
                <PasswordRequirement $met={hasLowerCase}>
                  <CheckCircle />
                  {t('auth.resetPassword.requirementLowerCase', 'Jedna mała litera')}
                </PasswordRequirement>
                <PasswordRequirement $met={hasNumber}>
                  <CheckCircle />
                  {t('auth.resetPassword.requirementNumber', 'Jedna cyfra')}
                </PasswordRequirement>
              </PasswordRequirements>
            </InputGroup>

            <InputGroup>
              <InputLabel>{t('auth.resetPassword.confirmPasswordLabel')}</InputLabel>
              <InputWrapper>
                <InputIcon>
                  <Lock />
                </InputIcon>
                <StyledInput
                  type={isConfirmPasswordVisible ? 'text' : 'password'}
                  placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
                  {...register('confirmPassword', {
                    required: t('auth.resetPassword.confirmPasswordRequired'),
                    validate: (value) => value === password || t('auth.resetPassword.passwordsDoNotMatch'),
                  })}
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setIsConfirmPasswordVisible(!isConfirmPasswordVisible)}
                >
                  {isConfirmPasswordVisible ? <EyeOff /> : <Eye />}
                </PasswordToggle>
              </InputWrapper>
              {errors.confirmPassword && <ErrorMessage>{errors.confirmPassword.message}</ErrorMessage>}

              {password && confirmPassword && (
                <PasswordRequirements style={{ marginTop: '8px', marginBottom: '0' }}>
                  <PasswordRequirement $met={passwordsMatch}>
                    <CheckCircle />
                    {passwordsMatch
                      ? t('auth.resetPassword.passwordsMatch', 'Hasła są takie same')
                      : t('auth.resetPassword.passwordsDoNotMatch', 'Hasła nie są takie same')}
                  </PasswordRequirement>
                </PasswordRequirements>
              )}
            </InputGroup>

            <SubmitButton type="submit" disabled={!isFormValid || isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner
                    size="sm"
                    classNames={{
                      circle1: 'border-b-white',
                      circle2: 'border-b-white',
                    }}
                  />
                  <span>{t('auth.resetPassword.resetting')}</span>
                </>
              ) : (
                t('auth.resetPassword.resetButton')
              )}
            </SubmitButton>
          </AuthForm>

          <SignUpLink>
            {t('auth.resetPassword.backToLogin')}
            {' '}
            <LocaleLink to="/login">{t('auth.resetPassword.signIn')}</LocaleLink>
          </SignUpLink>
        </FormWrapper>
      </FormContainer>

      <MarketingSection>
        <MarketingContent>
          <MarketingTitle>
            {t('auth.resetPassword.marketingTitle')}
          </MarketingTitle>
          <MarketingDescription>
            {t('auth.resetPassword.marketingDescription')}
          </MarketingDescription>
        </MarketingContent>
      </MarketingSection>
    </AuthContainer>
  );
};
